import { CHANNELS } from "../../shared/channels";
import {
  MAX_SLICES,
  PRESETS,
  RouletteState,
  SLICE_COLOURS,
  SPIN_SECONDS,
  Slice,
  Wheel,
  cleanEmoji,
  cleanWeight,
  liveSlices,
  parseEntry,
  pickWeighted,
  totalWeight
} from "../../shared/roulette";
import { CharacterSheet } from "../../shared/types";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, flashToast } from "../dom";
import { locale, t } from "../i18n";
import { onViewShown } from "../navigation";
import { buzz } from "../tweens";
import { canUse } from "../me";
import { reducedMotion } from "../motion_pref";

// a prize wheel on a hardware bezel: slices sized by their odds, a flapper that rides the pegs, chasing bulbs,
// clicks and a jingle. the winner is picked first with the secure random, then the wheel is aimed to land on it

const TAU = Math.PI * 2;
const WHEEL_KEY = "roulette-wheel";
const BULBS = 28;
// no two pegs further apart than this, so even a two slice wheel clicks like a real one
const PEG_GAP = TAU / 24;

interface Arc {
  slice: Slice;
  start: number;
  end: number;
  colour: string;
  ink: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  spin: number;
  turn: number;
  w: number;
  h: number;
  colour: string;
}

let state: RouletteState = { wheels: [] };
let wheelId = readStored();
let pending: RouletteState | null = null;
let saveTimer = 0;
let booted = false;

let angle = 0;
let spinning = false;
// the winner stays on the wheel until the party's over, even on a knockout wheel
let holding = false;
let winner: Arc | null = null;
let celebrateUntil = 0;
let flap = 0;
let lastPeg = -1;
let lastSliceId = "";

let arcs: Arc[] = [];
let pegs: number[] = [];
let face: HTMLCanvasElement | null = null;
let cssSize = 0;
let ratio = 1;
let palette: string[] = [];
let tokens = { bg: "#06080d", ink: "#e9eef7", ink3: "#748098", well: "#030509", panel: "#0d111b", line: "rgba(205,220,255,0.26)", chi: "#4de8ff", red: "#ff4458" };

let audio: AudioContext | null = null;
let lastTickAt = 0;

let confetti: Particle[] = [];
let confettiFrame = 0;

const wheelCanvas = () => byId<HTMLCanvasElement>("roulette-wheel");

function readStored(): string {
  try {
    return localStorage.getItem(WHEEL_KEY) || "";
  } catch {
    return "";
  }
}

function remember(id: string): void {
  wheelId = id;
  try {
    localStorage.setItem(WHEEL_KEY, id);
  } catch {
    // private mode keeps it for this visit
  }
}

function newId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(5));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

// a uniform number in [0, 1) from the device's secure random
function secureUnit(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
}

function calm(): boolean {
  return document.documentElement.classList.contains("calm") || matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function current(): Wheel | null {
  return state.wheels.find((wheel) => wheel.id === wheelId) || state.wheels[0] || null;
}

const mod = (value: number) => ((value % TAU) + TAU) % TAU;

// ---------- colours ----------

function rgb(colour: string): [number, number, number] {
  const probe = document.createElement("canvas").getContext("2d")!;
  probe.fillStyle = "#000";
  probe.fillStyle = colour;
  const said = String(probe.fillStyle);
  if (said.startsWith("#")) return [1, 3, 5].map((at) => parseInt(said.slice(at, at + 2), 16)) as [number, number, number];
  const parts = said.match(/[\d.]+/g)?.map(Number) || [0, 0, 0];
  return [parts[0], parts[1], parts[2]];
}

function mix(a: string, b: string, amount: number): string {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const at = (from: number, to: number) => Math.round(from + (to - from) * amount);
  return `rgb(${at(ar, br)}, ${at(ag, bg)}, ${at(ab, bb)})`;
}

function inkFor(colour: string): string {
  const [r, g, b] = rgb(colour).map((value) => {
    const unit = value / 255;
    return unit <= 0.03928 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.32 ? "#0b0d12" : "#ffffff";
}

function readTokens(): void {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  tokens = {
    bg: read("--bg", tokens.bg),
    ink: read("--ink", tokens.ink),
    ink3: read("--ink-3", tokens.ink3),
    well: read("--well", tokens.well),
    panel: read("--panel-solid", tokens.panel),
    line: read("--line-2", tokens.line),
    chi: read("--chi", tokens.chi),
    red: read("--red", tokens.red)
  };
  const bases = ["--chi", "--violet", "--amber", "--red", "--green"].map((name) => read(name, "#888"));
  // the theme's five colours, then the same five sunk toward the background
  palette = [...bases, ...bases.map((colour) => mix(colour, tokens.bg, 0.45))];
}

// ---------- geometry ----------

function layout(): void {
  const wheel = current();
  const live = wheel ? liveSlices(wheel) : [];
  const total = totalWeight(live);
  arcs = [];
  pegs = [];
  let at = 0;
  live.forEach((slice, index) => {
    const span = total > 0 ? (slice.weight / total) * TAU : 0;
    let colour = slice.colour ? SLICE_COLOURS[slice.colour] : palette[index % palette.length];
    // the last auto slice never matches the first one it touches
    if (!slice.colour && index === live.length - 1 && live.length > 1 && !live[0].colour && colour === palette[0]) colour = palette[(index + 1) % palette.length] === colour ? palette[2] : palette[(index + 1) % palette.length];
    arcs.push({ slice, start: at, end: at + span, colour, ink: inkFor(colour) });
    const count = Math.max(1, Math.ceil(span / PEG_GAP));
    for (let step = 0; step < count; step++) pegs.push(at + (span * step) / count);
    at += span;
  });
}

function arcAt(position: number): Arc | null {
  return arcs.find((arc) => position >= arc.start && position < arc.end) || arcs[arcs.length - 1] || null;
}

function pegIndexAt(position: number): number {
  let found = -1;
  for (let at = 0; at < pegs.length; at++) {
    if (pegs[at] <= position) found = at;
    else break;
  }
  return found;
}

function nearestPegGap(position: number): number {
  let best = TAU;
  for (const peg of pegs) {
    const gap = Math.abs(position - peg);
    best = Math.min(best, gap, TAU - gap);
  }
  return best;
}

// ---------- drawing ----------

function measure(): void {
  const canvas = wheelCanvas();
  const box = canvas.getBoundingClientRect();
  cssSize = Math.max(120, Math.round(box.width));
  ratio = Math.min(3, window.devicePixelRatio || 1);
  canvas.width = Math.round(cssSize * ratio);
  canvas.height = Math.round(cssSize * ratio);
}

const faceRadius = () => cssSize * 0.43;

function fitText(context: CanvasRenderingContext2D, text: string, width: number): string {
  if (context.measureText(text).width <= width) return text;
  const letters = Array.from(text);
  while (letters.length > 1 && context.measureText(`${letters.join("")}…`).width > width) letters.pop();
  return `${letters.join("")}…`;
}

// the turning part, drawn once per change and spun as a picture
function paintFace(): void {
  if (!cssSize) return;
  const radius = faceRadius();
  const canvas = face || document.createElement("canvas");
  face = canvas;
  canvas.width = Math.round(radius * 2 * ratio) + 4;
  canvas.height = canvas.width;
  const context = canvas.getContext("2d")!;
  context.setTransform(ratio, 0, 0, ratio, canvas.width / 2, canvas.height / 2);
  context.clearRect(-radius - 2, -radius - 2, radius * 2 + 4, radius * 2 + 4);
  if (!arcs.length) {
    context.beginPath();
    context.arc(0, 0, radius, 0, TAU);
    context.fillStyle = tokens.well;
    context.fill();
    context.setLineDash([6, 6]);
    context.strokeStyle = tokens.line;
    context.lineWidth = 1.5;
    context.stroke();
    return;
  }
  for (const arc of arcs) {
    context.beginPath();
    context.moveTo(0, 0);
    context.arc(0, 0, radius, arc.start - Math.PI / 2, arc.end - Math.PI / 2);
    context.closePath();
    context.fillStyle = arc.colour;
    context.fill();
  }
  // seams between slices
  if (arcs.length > 1) {
    context.strokeStyle = "rgba(0, 0, 0, 0.35)";
    context.lineWidth = 1.2;
    for (const arc of arcs) {
      context.beginPath();
      context.moveTo(0, 0);
      context.lineTo(Math.cos(arc.start - Math.PI / 2) * radius, Math.sin(arc.start - Math.PI / 2) * radius);
      context.stroke();
    }
  }
  // a printed inner ring, like the decal on a real wheel
  context.beginPath();
  context.arc(0, 0, radius * 0.93, 0, TAU);
  context.strokeStyle = "rgba(255, 255, 255, 0.16)";
  context.lineWidth = 1;
  context.stroke();
  // labels read from the hub out to the rim
  for (const arc of arcs) {
    const span = arc.end - arc.start;
    const room = span * radius * 0.62;
    if (room < 9) continue;
    // the text runs from the rim in to just short of the hub, shrinking a little before it gets cut
    const width = radius * 0.86 - cssSize * 0.11 - 8;
    const text = `${arc.slice.label}${arc.slice.emoji ? `  ${arc.slice.emoji}` : ""}`;
    let fontSize = Math.max(9, Math.min(radius * 0.095, room * 0.62, 26));
    context.font = `700 ${fontSize}px Rajdhani, "Arial Narrow", sans-serif`;
    while (fontSize > 11 && context.measureText(text).width > width) {
      fontSize -= 1;
      context.font = `700 ${fontSize}px Rajdhani, "Arial Narrow", sans-serif`;
    }
    context.save();
    context.rotate((arc.start + arc.end) / 2 - Math.PI / 2);
    context.textAlign = "right";
    context.textBaseline = "middle";
    context.fillStyle = arc.ink;
    context.shadowColor = arc.ink === "#ffffff" ? "rgba(0, 0, 0, 0.45)" : "rgba(255, 255, 255, 0.25)";
    context.shadowBlur = 2;
    context.fillText(fitText(context, text, width), radius * 0.86, 0);
    context.restore();
  }
  // pegs on the rim, the flapper clicks over these
  for (const peg of pegs) {
    const x = Math.cos(peg - Math.PI / 2) * radius * 0.965;
    const y = Math.sin(peg - Math.PI / 2) * radius * 0.965;
    context.beginPath();
    context.arc(x, y + 0.8, cssSize * 0.008, 0, TAU);
    context.fillStyle = "rgba(0, 0, 0, 0.5)";
    context.fill();
    context.beginPath();
    context.arc(x, y, cssSize * 0.008, 0, TAU);
    context.fillStyle = "#e9e4d6";
    context.fill();
  }
}

function bulbLit(index: number, now: number): boolean {
  if (celebrateUntil > now) return Math.floor(now / 140) % 2 === (index % 2);
  if (spinning) return (index + Math.floor(Math.abs(angle) * 3)) % 4 === 0;
  return index % 2 === 0;
}

function draw(): void {
  const canvas = wheelCanvas();
  const context = canvas.getContext("2d");
  if (!context || !cssSize) return;
  const now = performance.now();
  const centre = cssSize / 2;
  const radius = faceRadius();
  const outer = cssSize * 0.495;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, cssSize, cssSize);

  // the bezel, a dark ring with a bevel
  const bezel = context.createLinearGradient(0, 0, 0, cssSize);
  bezel.addColorStop(0, mix(tokens.panel, "#ffffff", 0.12));
  bezel.addColorStop(1, mix(tokens.panel, "#000000", 0.35));
  context.beginPath();
  context.arc(centre, centre, outer, 0, TAU);
  context.fillStyle = bezel;
  context.fill();
  context.strokeStyle = tokens.line;
  context.lineWidth = 1;
  context.stroke();
  context.beginPath();
  context.arc(centre, centre, radius + cssSize * 0.012, 0, TAU);
  context.fillStyle = tokens.well;
  context.fill();

  // bulbs that chase while it spins and flash the winner's colour after
  const bulbRadius = (radius + outer) / 2 + cssSize * 0.004;
  const lit = celebrateUntil > now && winner ? winner.colour : tokens.chi;
  for (let index = 0; index < BULBS; index++) {
    const turn = (index / BULBS) * TAU - Math.PI / 2;
    const x = centre + Math.cos(turn) * bulbRadius;
    const y = centre + Math.sin(turn) * bulbRadius;
    const on = bulbLit(index, now);
    context.beginPath();
    context.arc(x, y, cssSize * 0.011, 0, TAU);
    if (on) {
      context.shadowColor = lit;
      context.shadowBlur = cssSize * 0.03;
      context.fillStyle = lit;
    } else {
      context.shadowBlur = 0;
      context.fillStyle = mix(tokens.well, tokens.ink3, 0.35);
    }
    context.fill();
  }
  context.shadowBlur = 0;

  // the wheel itself
  if (face) {
    context.save();
    context.translate(centre, centre);
    context.rotate(angle);
    context.drawImage(face, -face.width / ratio / 2, -face.height / ratio / 2, face.width / ratio, face.height / ratio);
    // everything but the winner sinks back while it celebrates
    if (winner && celebrateUntil > now) {
      for (const arc of arcs) {
        if (arc === winner) continue;
        context.beginPath();
        context.moveTo(0, 0);
        context.arc(0, 0, radius, arc.start - Math.PI / 2, arc.end - Math.PI / 2);
        context.closePath();
        context.fillStyle = "rgba(0, 0, 0, 0.5)";
        context.fill();
      }
    }
    context.restore();
  }

  // a fixed light on top so the turning face reads as glossy plastic
  const gloss = context.createRadialGradient(centre - radius * 0.35, centre - radius * 0.45, radius * 0.05, centre, centre, radius);
  gloss.addColorStop(0, "rgba(255, 255, 255, 0.22)");
  gloss.addColorStop(0.45, "rgba(255, 255, 255, 0.04)");
  gloss.addColorStop(1, "rgba(0, 0, 0, 0.28)");
  context.beginPath();
  context.arc(centre, centre, radius, 0, TAU);
  context.fillStyle = gloss;
  context.fill();

  byId("roulette-flap").style.transform = `rotate(${flap.toFixed(2)}deg)`;
}

function rebuild(): void {
  readTokens();
  layout();
  paintFace();
  draw();
  const wheel = current();
  const live = wheel ? liveSlices(wheel) : [];
  const empty = byId("roulette-wheel-empty");
  empty.hidden = live.length >= 2;
  empty.textContent = !wheel || !wheel.slices.length ? t("add a couple of options") : live.length === 1 ? t("last one standing: {label}", { label: live[0].label }) : live.length ? "" : t("everything's been knocked out");
  byId<HTMLButtonElement>("roulette-spin").disabled = live.length < 2;
  byId<HTMLButtonElement>("roulette-hub").disabled = live.length < 2;
  byId("roulette-restore").hidden = !wheel?.slices.some((slice) => slice.out);
}

// ---------- sound ----------

function wakeAudio(): void {
  if (!current()?.sound) return;
  try {
    audio ??= new AudioContext();
    if (audio.state === "suspended") void audio.resume();
  } catch {
    audio = null;
  }
}

function blip(frequency: number, seconds: number, volume: number, type: OscillatorType, delay = 0): void {
  if (!audio || !current()?.sound) return;
  const start = audio.currentTime + delay;
  const tone = audio.createOscillator();
  const gain = audio.createGain();
  tone.type = type;
  tone.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(volume, start);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + seconds);
  tone.connect(gain).connect(audio.destination);
  tone.start(start);
  tone.stop(start + seconds + 0.02);
}

function tick(): void {
  const now = performance.now();
  if (now - lastTickAt < 28) return;
  lastTickAt = now;
  blip(1700 + secureUnit() * 300, 0.02, 0.05, "square");
  buzz(4);
}

function jingle(): void {
  [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((note, index) => blip(note, 0.16, 0.06, index === 4 ? "triangle" : "square", index * 0.085));
}

// ---------- spinning ----------

const easeOut = (x: number) => 1 - (1 - x) ** 4;

function readout(tag: string, text: string, colour = ""): void {
  byId("roulette-readout-tag").textContent = tag;
  byId("roulette-now").textContent = text;
  const box = byId("roulette-readout");
  box.style.setProperty("--win", colour || "var(--chi)");
}

function follow(direction: number): void {
  const position = mod(-angle);
  const peg = pegIndexAt(position);
  if (peg !== lastPeg) {
    if (lastPeg !== -1) tick();
    lastPeg = peg;
  }
  // the flapper bends away from a peg passing under it, then springs back
  const near = Math.max(0, 1 - (nearestPegGap(position) * faceRadius()) / (cssSize * 0.045));
  const target = -direction * 28 * near;
  flap = Math.abs(target) > Math.abs(flap) ? target : flap + (target - flap) * 0.3;
  const arc = arcAt(position);
  if (arc && arc.slice.id !== lastSliceId) {
    lastSliceId = arc.slice.id;
    byId("roulette-now").textContent = `${arc.slice.emoji ? `${arc.slice.emoji} ` : ""}${arc.slice.label}`;
  }
}

function spin(velocity = 0, windUp = true): void {
  let wheel = current();
  // spinning again cuts the party short, once a knocked out winner has really left
  if (wheel && holding && (pending || wheel.mode === "keep")) {
    celebrateUntil = 0;
    settle();
    wheel = current();
  }
  if (!wheel || spinning || holding) return;
  const live = liveSlices(wheel);
  if (live.length < 2) return;
  wakeAudio();
  const picked = live[pickWeighted(live, secureUnit())];
  const arc = arcs.find((item) => item.slice.id === picked.id);
  if (!arc) return;
  // land between two pegs inside the winner, never on one
  const edges = [arc.start, ...pegs.filter((peg) => peg > arc.start + 1e-9 && peg < arc.end - 1e-9), arc.end];
  const cell = Math.min(edges.length - 2, Math.floor(secureUnit() * (edges.length - 1)));
  const spot = edges[cell] + (edges[cell + 1] - edges[cell]) * (0.25 + 0.5 * secureUnit());

  const quiet = calm();
  const direction = velocity < 0 ? -1 : 1;
  const seconds = quiet ? 1 : SPIN_SECONDS[wheel.length];
  const speed = Math.min(42, Math.max(14, Math.abs(velocity) || 24 + secureUnit() * 6));
  const turns = quiet ? 1 : Math.max(2, Math.round((speed * seconds) / 4 / TAU));
  const back = windUp && !quiet ? 0.14 : 0;
  const from = angle - back * direction;
  const to = direction > 0 ? from + turns * TAU + mod(-spot - from) : from - turns * TAU - mod(from + spot);
  const start = angle;
  const windMs = back ? 240 : 0;
  const spinMs = seconds * 1000;
  const began = performance.now();

  spinning = true;
  winner = null;
  celebrateUntil = 0;
  lastSliceId = "";
  lastPeg = pegIndexAt(mod(-angle));
  document.body.classList.add("roulette-spinning");
  byId("roulette-readout").classList.remove("won");
  byId("roulette-readout").classList.add("live");
  readout(t("spinning"), "…");

  const step = (now: number) => {
    const gone = now - began;
    if (gone < windMs) {
      const p = gone / windMs;
      angle = start + (from - start) * Math.sin((p * Math.PI) / 2);
      follow(-direction);
    } else {
      const p = Math.min(1, (gone - windMs) / spinMs);
      angle = from + (to - from) * easeOut(p);
      follow(direction);
      if (p >= 1) {
        angle = mod(to);
        land(arc);
        return;
      }
    }
    draw();
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function land(arc: Arc): void {
  spinning = false;
  holding = true;
  winner = arc;
  flap = 0;
  document.body.classList.remove("roulette-spinning");
  const quiet = calm();
  celebrateUntil = performance.now() + (quiet ? 1200 : 2600);
  const box = byId("roulette-readout");
  box.classList.remove("live");
  readout(t("the wheel says"), `${arc.slice.emoji ? `${arc.slice.emoji} ` : ""}${arc.slice.label}`, arc.colour);
  box.classList.remove("won");
  void box.offsetWidth;
  box.classList.add("won");
  jingle();
  buzz(30);
  if (!quiet) burst(arc.colour);
  const wheel = current();
  if (wheel) {
    call<RouletteState>(CHANNELS.rouletteSpun, wheel.id, arc.slice.id)
      .then((next) => { pending = next; })
      .catch((error) => flashToast(t(String(error).replace(/^Error: /, ""))));
  }
  const glow = () => {
    draw();
    if (!holding) return;
    if (performance.now() < celebrateUntil) requestAnimationFrame(glow);
    else settle();
  };
  requestAnimationFrame(glow);
}

// the party's over: the knocked out slice can leave now
function settle(): void {
  if (!holding) return;
  holding = false;
  winner = null;
  draw();
  if (pending) {
    const next = pending;
    pending = null;
    apply(next);
  }
}

// a flick spins it the way it was thrown, as hard as it was thrown
function setupFlick(): void {
  const canvas = wheelCanvas();
  let dragging = false;
  let last = 0;
  let moved = 0;
  let samples: { at: number; angle: number }[] = [];
  const pointerAngle = (event: PointerEvent) => {
    const box = canvas.getBoundingClientRect();
    return Math.atan2(event.clientY - box.top - box.height / 2, event.clientX - box.left - box.width / 2);
  };
  canvas.addEventListener("pointerdown", (event) => {
    if (spinning || holding || arcs.length < 2) return;
    dragging = true;
    moved = 0;
    last = pointerAngle(event);
    samples = [{ at: performance.now(), angle }];
    canvas.setPointerCapture(event.pointerId);
    wakeAudio();
  });
  canvas.addEventListener("pointermove", (event) => {
    if (!dragging) return;
    const now = pointerAngle(event);
    let delta = now - last;
    if (delta > Math.PI) delta -= TAU;
    if (delta < -Math.PI) delta += TAU;
    last = now;
    angle += delta;
    moved += Math.abs(delta);
    const at = performance.now();
    samples.push({ at, angle });
    samples = samples.filter((sample) => at - sample.at < 120);
    follow(Math.sign(delta) || 1);
    draw();
  });
  const release = (event: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    flap = 0;
    const first = samples[0];
    const end = samples[samples.length - 1];
    const seconds = first && end ? (end.at - first.at) / 1000 : 0;
    const velocity = seconds > 0.01 ? (end.angle - first.angle) / seconds : 0;
    if (Math.abs(velocity) > 4 && moved > 0.15) spin(velocity, false);
    else draw();
  };
  canvas.addEventListener("pointerup", release);
  canvas.addEventListener("pointercancel", release);
  canvas.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    spin();
  });
}

// ---------- confetti ----------

function burst(colour: string): void {
  if (reducedMotion()) return;
  const canvas = byId<HTMLCanvasElement>("roulette-confetti");
  const stage = byId("roulette-stage").getBoundingClientRect();
  const machine = byId("roulette-machine").getBoundingClientRect();
  canvas.width = Math.round(stage.width * ratio);
  canvas.height = Math.round(stage.height * ratio);
  const originX = machine.left - stage.left + machine.width / 2;
  const originY = machine.top - stage.top + machine.height / 2;
  const colours = [colour, colour, tokens.chi, ...arcs.map((arc) => arc.colour)];
  confetti = Array.from({ length: 150 }, () => {
    const heading = -Math.PI / 2 + (secureUnit() - 0.5) * Math.PI * 1.3;
    const power = 5 + secureUnit() * 9;
    return {
      x: originX,
      y: originY,
      vx: Math.cos(heading) * power,
      vy: Math.sin(heading) * power - 2,
      spin: (secureUnit() - 0.5) * 0.4,
      turn: secureUnit() * TAU,
      w: 4 + secureUnit() * 5,
      h: 7 + secureUnit() * 7,
      colour: colours[Math.floor(secureUnit() * colours.length)]
    };
  });
  cancelAnimationFrame(confettiFrame);
  const began = performance.now();
  const step = (now: number) => {
    const context = canvas.getContext("2d")!;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, stage.width, stage.height);
    const fade = Math.max(0, 1 - (now - began - 1600) / 900);
    for (const bit of confetti) {
      bit.vy += 0.32;
      bit.vx *= 0.985;
      bit.vy *= 0.985;
      bit.x += bit.vx;
      bit.y += bit.vy;
      bit.turn += bit.spin;
      context.save();
      context.globalAlpha = fade;
      context.translate(bit.x, bit.y);
      context.rotate(bit.turn);
      context.scale(1, Math.cos(bit.turn * 2));
      context.fillStyle = bit.colour;
      context.fillRect(-bit.w / 2, -bit.h / 2, bit.w, bit.h);
      context.restore();
    }
    if (fade > 0) confettiFrame = requestAnimationFrame(step);
    else context.clearRect(0, 0, stage.width, stage.height);
  };
  confettiFrame = requestAnimationFrame(step);
}

// ---------- saving ----------

function queueSave(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => void saveNow(), 450);
}

async function saveNow(): Promise<void> {
  window.clearTimeout(saveTimer);
  saveTimer = 0;
  const wheel = current();
  if (!wheel) return;
  try {
    const next = await call<RouletteState>(CHANNELS.rouletteSave, wheel);
    // keep what's on screen, only take wheels that changed elsewhere
    if (!saveTimer) state = { wheels: next.wheels.map((item) => item.id === wheel.id ? current() || item : item) };
  } catch (error) {
    flashToast(t(String(error).replace(/^Error: /, "")));
  }
}

// a whole wheel changed: the drawing, the list and the switches all follow
function changed(): void {
  rebuild();
  renderSide();
  queueSave();
}

// someone's other screen changed a wheel. wait while this one is mid spin or mid edit
function apply(next: RouletteState): void {
  const editing = byId("view-roulette").contains(document.activeElement) && document.activeElement?.matches("input, textarea");
  if (spinning || holding || saveTimer || editing) {
    pending = next;
    return;
  }
  // our own save coming back, nothing to redraw
  if (JSON.stringify(next) === JSON.stringify(state)) return;
  state = next;
  if (!state.wheels.some((wheel) => wheel.id === wheelId) && state.wheels[0]) remember(state.wheels[0].id);
  renderAll();
}

async function makeWheel(raw: Partial<Wheel>): Promise<void> {
  const wheel: Wheel = {
    id: newId(),
    name: raw.name || t("new wheel"),
    slices: raw.slices || [],
    mode: raw.mode || "keep",
    length: raw.length || "normal",
    sound: raw.sound ?? true,
    history: [],
    updatedAt: new Date().toISOString()
  };
  try {
    state = await call<RouletteState>(CHANNELS.rouletteSave, wheel);
    remember(wheel.id);
    angle = 0;
    renderAll();
  } catch (error) {
    flashToast(t(String(error).replace(/^Error: /, "")));
  }
}

// preset labels are words, so they are translated as the wheel is made. after that they are the person's own
function slicesFrom(list: Array<[string, string?, number?]>, words = false): Slice[] {
  return list.map(([label, emoji, weight]) => ({ id: newId(), label: words ? t(label) : label, emoji: emoji || "", weight: weight || 1, colour: "", out: false }));
}

// ---------- the side panel ----------

function percentOf(slice: Slice, live: Slice[]): string {
  if (slice.out) return t("knocked out");
  const total = totalWeight(live);
  const share = total > 0 ? (slice.weight / total) * 100 : 0;
  return share >= 10 || share === 0 ? `${Math.round(share)}%` : `${share.toFixed(1)}%`;
}

function refreshOdds(): void {
  const wheel = current();
  if (!wheel) return;
  const live = liveSlices(wheel);
  const total = totalWeight(live);
  byId("roulette-slices").querySelectorAll<HTMLElement>(".roulette-slice").forEach((row) => {
    const slice = wheel.slices.find((item) => item.id === row.dataset.id);
    if (!slice) return;
    row.querySelector<HTMLElement>("[data-role=percent]")!.textContent = percentOf(slice, live);
    row.querySelector<HTMLElement>("[data-role=bar]")!.style.width = slice.out || !total ? "0%" : `${(slice.weight / total) * 100}%`;
  });
  const out = wheel.slices.length - live.length;
  byId("roulette-odds-note").textContent = wheel.slices.length ? out ? t("{n} on the wheel, {out} knocked out", { n: live.length, out }) : t("{n} on the wheel", { n: live.length }) : "";
}

function paintSwatch(button: HTMLElement, slice: Slice, index: number): void {
  const arc = arcs.find((item) => item.slice.id === slice.id);
  button.style.background = slice.colour ? SLICE_COLOURS[slice.colour] : arc?.colour || palette[index % palette.length];
  button.classList.toggle("auto", !slice.colour);
}

function sliceRow(slice: Slice, index: number): HTMLElement {
  const row = cloneTemplate("template-roulette-slice");
  row.dataset.id = slice.id;
  row.classList.toggle("is-out", slice.out);
  const part = <T extends HTMLElement>(role: string) => row.querySelector<T>(`[data-role=${role}]`)!;
  const label = part<HTMLInputElement>("label");
  const emoji = part<HTMLInputElement>("emoji");
  const weight = part<HTMLInputElement>("weight");
  const swatch = part<HTMLButtonElement>("swatch");
  const colours = part<HTMLElement>("colours");
  label.value = slice.label;
  emoji.value = slice.emoji;
  weight.value = String(slice.weight);
  paintSwatch(swatch, slice, index);

  const touched = () => {
    rebuild();
    refreshOdds();
    paintSwatch(swatch, slice, index);
    queueSave();
  };
  let before = slice.label;
  label.addEventListener("focus", () => { before = slice.label; });
  label.addEventListener("input", () => {
    if (!label.value.trim()) return;
    slice.label = label.value.replace(/\s+/g, " ").slice(0, 80);
    touched();
  });
  label.addEventListener("blur", () => {
    if (!label.value.trim()) label.value = slice.label = before;
  });
  emoji.addEventListener("input", () => {
    slice.emoji = cleanEmoji(emoji.value);
    touched();
  });
  emoji.addEventListener("blur", () => { emoji.value = slice.emoji; });
  const setWeight = (value: number) => {
    slice.weight = cleanWeight(value);
    weight.value = String(slice.weight);
    touched();
  };
  weight.addEventListener("change", () => setWeight(Number(weight.value.replace(",", "."))));
  part("less").addEventListener("click", () => setWeight(slice.weight > 1 ? Math.ceil(slice.weight) - 1 : slice.weight - 0.1));
  part("more").addEventListener("click", () => setWeight(slice.weight >= 1 ? Math.floor(slice.weight) + 1 : slice.weight + 0.1));
  part("remove").addEventListener("click", () => {
    const wheel = current();
    if (!wheel) return;
    wheel.slices = wheel.slices.filter((item) => item.id !== slice.id);
    changed();
  });
  swatch.addEventListener("click", () => {
    if (!colours.childElementCount) {
      const pick = (name: string) => {
        slice.colour = name;
        colours.querySelectorAll("button").forEach((button) => button.classList.toggle("active", button.dataset.colour === name));
        touched();
      };
      const theme = document.createElement("button");
      theme.type = "button";
      theme.className = "slice-colour theme";
      theme.dataset.colour = "";
      theme.textContent = t("auto");
      theme.addEventListener("click", () => pick(""));
      colours.append(theme);
      for (const [name, hex] of Object.entries(SLICE_COLOURS)) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "slice-colour";
        button.dataset.colour = name;
        button.style.background = hex;
        button.setAttribute("aria-label", t(name));
        button.addEventListener("click", () => pick(name));
        colours.append(button);
      }
    }
    colours.querySelectorAll<HTMLElement>("button").forEach((button) => button.classList.toggle("active", button.dataset.colour === slice.colour));
    colours.hidden = !colours.hidden;
    swatch.setAttribute("aria-expanded", String(!colours.hidden));
  });
  return row;
}

function renderSlices(): void {
  const wheel = current();
  const list = byId("roulette-slices");
  list.replaceChildren(...(wheel?.slices || []).map(sliceRow));
  byId("roulette-slices-empty").hidden = Boolean(wheel?.slices.length);
  refreshOdds();
}

function ago(at: string): string {
  const minutes = Math.floor((Date.now() - Date.parse(at)) / 60000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return t("just now");
  if (minutes < 60) return t("{n}m ago", { n: minutes });
  if (minutes < 1440) return t("{n}h ago", { n: Math.floor(minutes / 60) });
  return new Date(at).toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

function renderHistory(): void {
  const wheel = current();
  const history = wheel?.history || [];
  const counts = new Map<string, number>();
  history.forEach((spin) => counts.set(spin.label, (counts.get(spin.label) || 0) + 1));
  const top = Math.max(1, ...counts.values());
  byId("roulette-tally").replaceChildren(...[...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => {
    const row = document.createElement("li");
    row.className = "tally-line";
    const name = document.createElement("span");
    name.textContent = label;
    const bar = document.createElement("i");
    bar.style.width = `${(count / top) * 100}%`;
    const number = document.createElement("b");
    number.textContent = `${count}`;
    const track = document.createElement("div");
    track.className = "tally-track";
    track.append(bar);
    row.append(name, track, number);
    return row;
  }));
  byId("roulette-history").replaceChildren(...history.map((spin, index) => {
    const row = document.createElement("li");
    row.className = "history-line";
    const name = document.createElement("span");
    name.textContent = `${history.length - index}. ${spin.label}`;
    const when = document.createElement("small");
    when.textContent = ago(spin.at);
    row.append(name, when);
    return row;
  }));
  byId("roulette-history-empty").hidden = history.length > 0;
  byId("roulette-clear-history").hidden = !history.length;
}

function renderOptions(): void {
  const wheel = current();
  if (!wheel) return;
  const name = byId<HTMLInputElement>("roulette-name");
  if (document.activeElement !== name) name.value = wheel.name;
  byId("roulette-mode").querySelectorAll<HTMLElement>("[data-mode]").forEach((button) => button.classList.toggle("active", button.dataset.mode === wheel.mode));
  byId("roulette-length").querySelectorAll<HTMLElement>("[data-length]").forEach((button) => button.classList.toggle("active", button.dataset.length === wheel.length));
  byId<HTMLInputElement>("roulette-sound").checked = wheel.sound;
}

function renderPicker(): void {
  const select = byId<HTMLSelectElement>("roulette-pick");
  select.replaceChildren(...state.wheels.map((wheel) => {
    const option = document.createElement("option");
    option.value = wheel.id;
    option.textContent = wheel.name;
    return option;
  }));
  select.value = current()?.id || "";
}

function renderSide(): void {
  renderPicker();
  renderSlices();
  renderHistory();
  renderOptions();
}

function renderAll(): void {
  const wheel = current();
  if (wheel && wheel.id !== wheelId) remember(wheel.id);
  rebuild();
  renderSide();
  if (!spinning && !holding) {
    byId("roulette-readout").classList.remove("won", "live");
    readout(t("ready"), liveSlices(wheel || ({ slices: [] } as unknown as Wheel)).length >= 2 ? t("flick the wheel or press spin") : t("add a couple of options to spin"));
  }
}

function showTab(tab: string): void {
  byId("roulette-tabs").querySelectorAll<HTMLElement>("[data-tab]").forEach((button) => button.classList.toggle("active", button.dataset.tab === tab));
  byId("view-roulette").querySelectorAll<HTMLElement>("[data-pane]").forEach((pane) => { pane.hidden = pane.dataset.pane !== tab; });
}

function addEntries(text: string): void {
  const wheel = current();
  if (!wheel) return;
  const room = MAX_SLICES - wheel.slices.length;
  const lines = text.split(/\n|,(?=\s*\S)/).map(parseEntry).filter((entry): entry is { label: string; weight: number } => Boolean(entry));
  if (!lines.length) return;
  if (room <= 0) return flashToast(t("a wheel holds {max} options", { max: MAX_SLICES }));
  wheel.slices.push(...lines.slice(0, room).map((entry) => ({ id: newId(), label: entry.label.slice(0, 80), weight: entry.weight, emoji: "", colour: "", out: false })));
  if (lines.length > room) flashToast(t("only {room} fit, a wheel holds {max}", { room, max: MAX_SLICES }));
  changed();
}

function setupControls(): void {
  byId("roulette-spin").addEventListener("click", () => spin());
  byId("roulette-hub").addEventListener("click", () => spin());
  byId("roulette-restore").addEventListener("click", () => {
    const wheel = current();
    if (!wheel) return;
    wheel.slices.forEach((slice) => { slice.out = false; });
    changed();
  });
  byId("roulette-pick").addEventListener("change", () => {
    if (spinning || holding) return;
    remember(byId<HTMLSelectElement>("roulette-pick").value);
    angle = 0;
    renderAll();
  });
  byId("roulette-new").addEventListener("click", async () => {
    await makeWheel({ name: t("new wheel") });
    showTab("slices");
    byId("roulette-add-text").focus();
  });
  byId("roulette-tabs").addEventListener("click", (event) => {
    const tab = (event.target as HTMLElement).closest<HTMLElement>("[data-tab]")?.dataset.tab;
    if (tab) showTab(tab);
  });

  const addText = byId<HTMLTextAreaElement>("roulette-add-text");
  const grow = () => {
    addText.style.height = "auto";
    addText.style.height = `${Math.min(140, addText.scrollHeight)}px`;
  };
  addText.addEventListener("input", grow);
  addText.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      byId<HTMLFormElement>("roulette-add").requestSubmit();
    }
  });
  byId("roulette-add").addEventListener("submit", (event) => {
    event.preventDefault();
    addEntries(addText.value);
    addText.value = "";
    grow();
  });

  byId("roulette-shuffle").addEventListener("click", () => {
    const wheel = current();
    if (!wheel || spinning || holding) return;
    for (let at = wheel.slices.length - 1; at > 0; at--) {
      const swap = Math.floor(secureUnit() * (at + 1));
      [wheel.slices[at], wheel.slices[swap]] = [wheel.slices[swap], wheel.slices[at]];
    }
    changed();
  });
  byId("roulette-even").addEventListener("click", () => {
    const wheel = current();
    if (!wheel || spinning || holding) return;
    wheel.slices.forEach((slice) => { slice.weight = 1; });
    changed();
  });
  byId("roulette-recolour").addEventListener("click", () => {
    const wheel = current();
    if (!wheel || spinning || holding) return;
    wheel.slices.forEach((slice) => { slice.colour = ""; });
    changed();
  });

  byId("roulette-clear-history").addEventListener("click", () => {
    const wheel = current();
    if (!wheel) return;
    wheel.history = [];
    renderHistory();
    queueSave();
  });

  byId("roulette-name").addEventListener("input", () => {
    const wheel = current();
    const value = byId<HTMLInputElement>("roulette-name").value.trim();
    if (!wheel || !value) return;
    wheel.name = value.slice(0, 60);
    renderPicker();
    queueSave();
  });
  byId("roulette-mode").addEventListener("click", (event) => {
    const mode = (event.target as HTMLElement).closest<HTMLElement>("[data-mode]")?.dataset.mode;
    const wheel = current();
    if (!wheel || (mode !== "keep" && mode !== "knockout")) return;
    wheel.mode = mode;
    renderOptions();
    queueSave();
  });
  byId("roulette-length").addEventListener("click", (event) => {
    const length = (event.target as HTMLElement).closest<HTMLElement>("[data-length]")?.dataset.length;
    const wheel = current();
    if (!wheel || (length !== "quick" && length !== "normal" && length !== "long")) return;
    wheel.length = length;
    renderOptions();
    queueSave();
  });
  byId("roulette-sound").addEventListener("change", () => {
    const wheel = current();
    if (!wheel) return;
    wheel.sound = byId<HTMLInputElement>("roulette-sound").checked;
    if (wheel.sound) {
      wakeAudio();
      tick();
    }
    queueSave();
  });

  const presets = byId("roulette-presets");
  presets.replaceChildren(...PRESETS.map((preset) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.textContent = t(preset.name);
    chip.addEventListener("click", () => void makeWheel({ name: t(preset.name), slices: slicesFrom(preset.slices, true) }).then(() => showTab("slices")));
    return chip;
  }));
  if (canUse("ocs")) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.textContent = t("my characters");
    chip.addEventListener("click", async () => {
      try {
        const sheets = await call<CharacterSheet[]>(CHANNELS.ocsList);
        const names = [...new Set(sheets.map((sheet) => sheet.name.trim()).filter(Boolean))].slice(0, MAX_SLICES);
        if (names.length < 2) return flashToast(t("there aren't enough characters yet"));
        await makeWheel({ name: t("which oc"), slices: slicesFrom(names.map((name) => [name])) });
        showTab("slices");
      } catch (error) {
        flashToast(t(String(error).replace(/^Error: /, "")));
      }
    });
    presets.append(chip);
  }

  byId("roulette-copy").addEventListener("click", () => {
    const wheel = current();
    if (!wheel) return;
    void makeWheel({ ...wheel, name: t("{name} copy", { name: wheel.name }).slice(0, 60), slices: wheel.slices.map((slice) => ({ ...slice, id: newId(), out: false })) });
  });
  byId("roulette-remove").addEventListener("click", async () => {
    const wheel = current();
    if (!wheel || spinning || holding) return;
    if (!confirm(t("delete the wheel \"{name}\"?", { name: wheel.name }))) return;
    try {
      window.clearTimeout(saveTimer);
      saveTimer = 0;
      state = await call<RouletteState>(CHANNELS.rouletteRemove, wheel.id);
      remember(state.wheels[0]?.id || "");
      angle = 0;
      if (!state.wheels.length) await starter();
      else renderAll();
    } catch (error) {
      flashToast(t(String(error).replace(/^Error: /, "")));
    }
  });
}

// someone's first visit gets a wheel to play with straight away
async function starter(): Promise<void> {
  const preset = PRESETS.find((item) => item.name === "what to draw") || PRESETS[0];
  await makeWheel({ name: t(preset.name), slices: slicesFrom(preset.slices, true) });
}

async function load(): Promise<void> {
  try {
    state = await call<RouletteState>(CHANNELS.rouletteGet);
    if (!state.wheels.length) await starter();
    else renderAll();
  } catch (error) {
    flashToast(t(String(error).replace(/^Error: /, "")));
  }
}

export function setupRouletteView(): void {
  readTokens();
  setupControls();
  setupFlick();
  listen<RouletteState>(CHANNELS.rouletteUpdated, (next) => {
    if (booted) apply(next);
  });
  new ResizeObserver(() => {
    if (!byId("view-roulette").classList.contains("active")) return;
    measure();
    rebuild();
  }).observe(byId("roulette-machine"));
  // a new theme repaints the wheel in its colours
  new MutationObserver(() => {
    if (!booted) return;
    rebuild();
    if (!byId("roulette-slices").contains(document.activeElement)) renderSlices();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-tone"] });
  onViewShown("roulette", () => {
    measure();
    if (!booted) {
      booted = true;
      void load();
    } else if (pending && !spinning && !holding) {
      const next = pending;
      pending = null;
      apply(next);
    } else {
      rebuild();
    }
  });
}
